import React, { useEffect, useRef, useState } from 'react';
import toast from '../../../utils/toast';
import { RightArrow } from '../../icons/right-arrow';
import {
  Field,
  FieldError,
  Label,
  Input,
  FullWidthButton,
  ErrorText,
  HelperText,
} from '../../onboarding/onboarding.styled';
import { Shake, StatusLabel } from '../../../motion';
import {
  getOrganization,
  organizationNameError,
  renameOrganization,
} from '../../../api/organization-client';
import { SectionHeading, SectionLead } from './account.styled';

/**
 * THE COMPANY'S NAME (Plan 3 F2). Owner only — the tab exists only for
 * billing:manage, the same permission the server checks.
 *
 * Signup stored a placeholder; the owner is asked for the real name on the
 * first step of setup, and this is where it changes afterwards. While the
 * placeholder is still there the field is prefilled with the first building's
 * name, the way the setup step does it.
 */
export const AccountOrganization: React.FC = () => {
  const [name, setName] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);

  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  useEffect(() => {
    getOrganization()
      .then(org => {
        if (!alive.current) return;
        setSaved(org.name);
        setName(org.name || org.suggested_name || '');
      })
      .catch(() => {
        if (alive.current) setLoadError('We could not load your company name.');
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  }, []);

  const refuse = (message: string) => {
    setError(message);
    setAttempt(count => count + 1);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = name.replace(/\s+/g, ' ').trim();
    if (clean.length < 2) {
      refuse('Enter your company name.');

      return;
    }
    setError('');
    setSaving(true);
    try {
      const org = await renameOrganization(clean);
      if (!alive.current) return;
      setSaved(org.name);
      setName(org.name);
      toast.success('Company name saved');
    } catch (err) {
      if (alive.current) refuse(organizationNameError(err));
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  if (loading) {
    return <HelperText>Loading your company…</HelperText>;
  }
  if (loadError) {
    return <ErrorText>{loadError}</ErrorText>;
  }

  return (
    <>
      <SectionHeading>Company</SectionHeading>
      <SectionLead>
        {saved
          ? 'The name your team sees, and the one Lobbero support knows your account by.'
          : 'Your account does not have a company name yet. Your team sees this name, and so does Lobbero support when you get in touch.'}
      </SectionLead>

      <form noValidate onSubmit={save}>
        <Field>
          <Label htmlFor="ac-org-name">Company name</Label>
          <Shake active={!!error} attempt={attempt}>
            <Input
              id="ac-org-name"
              value={name}
              maxLength={120}
              autoComplete="organization"
              hasError={!!error}
              onChange={e => {
                setName(e.target.value);
                if (error) setError('');
              }}
            />
          </Shake>
        </Field>
        <FieldError message={error || undefined} />
        <FullWidthButton type="submit" disabled={saving} aria-busy={saving}>
          <StatusLabel
            state={saving ? 'busy' : 'idle'}
            labels={{
              idle: (
                <>
                  Save name <RightArrow />
                </>
              ),
              busy: 'Saving…',
            }}
          />
        </FullWidthButton>
      </form>
    </>
  );
};
